import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  FormGroup,
  FormControl,
  Select,
  Grid,
  MenuItem,
  Alert,
  Dialog,
  DialogContent,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { keyframes } from "@mui/system";
import GenerateIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoIcon from "@mui/icons-material/Info";
import QuestionTypeSelector from "./QuestionTypesHP";
import { AIModelSelect } from "./TestComponents2";
import { v4 as uuidv4 } from "uuid";
import { collection, doc, setDoc } from "firebase/firestore";
import LanguageSelector from "./LanguageSelector";
import { db } from "./firebase";
import { VideoPlayer } from "./HelpDialogItems";
import { trackLesson, BackButton } from "./functions";
import { AIModelInfoTooltip } from "./AIModelInfoDialog";
import QuestionMarkIcon from "@mui/icons-material/Help";
import { AnimatedStep } from "./Animations";
import InfoBox from "./InfoBox";

import { useNavigate } from "react-router-dom";
import { useSnackbar } from "./SnackbarHandler";
import { callApi } from "./api";
import GradeSelector, { gradeInstructions } from "./GradeSelector";
import Logger from "./Logger";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
`;

const GenerateHP = ({ user, theme, styles }) => {
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState([]);
  const [difficulty, setDifficulty] = useState("hard");
  const [selectedLanguage, setSelectedLanguage] = useState("svenska");
  const [selectedModel, setSelectedModel] = useState("Studera.AI");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState({});
  const [generatedTestId, setGeneratedTestId] = useState(null);
  const [grade, setGrade] = useState("åk8");
  const [progress, setProgress] = useState(0);
  const [currentGeneratingType, setCurrentGeneratingType] = useState("");

  const [selectedThemes, setSelectedThemes] = useState({
    ORD: "AI får välja",
    LÄS: "AI får välja",
    MEK: "AI får välja",
    ELF: "AI får välja",
    XYZ: "AI får välja",
    DTK: "AI får välja",
  });

  const [questionCounts, setQuestionCounts] = useState({
    ORD: 5,
    LÄS: 2,
    MEK: 3,
    ELF: 2,
    XYZ: 3,
    DTK: 3,
  });

  const questionTypeDescriptions = {
    ORD: "Ordförståelse",
    LÄS: "Läsförståelse",
    MEK: "Meningskomplettering",
    ELF: "Engelsk läsförståelse",
  };
  const [disableBackdropClick, setDisableBackdropClick] = useState(false);

  const handleBackdropClick = (event) => {
    event.stopPropagation();
    if (!disableBackdropClick) {
      showSnackbar("Genereringen är snart klar. Vänligen vänta.", "info");
    }
  };

  const progressRef = useRef(null);
  const progressPercentRef = useRef(null);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [currentStep, setCurrentStep] = useState(1);
  useEffect(() => {
    if (currentStep === 2) {
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      }, 0);
    }

    // if (currentStep === 2 && questionType) {
    //   setCurrentStep(3);
    //   setTimeout(() => {
    //     window.scrollTo({
    //       top: 0,
    //       behavior: "auto",
    //     });
    //   }, 0);
    // }
  }, [currentStep]);

  const handleQuestionTypeChange = (event) => {
    const { name, checked } = event.target;
    setSelectedQuestionTypes((prev) => {
      if (checked) {
        return [...prev, name];
      } else {
        return prev.filter((type) => type !== name);
        // Now it only removes the specific unchecked question type
      }
    });
  };
  const handleThemeChange = (type, theme) => {
    Logger.log("theme: ", theme);
    setSelectedThemes((prev) => ({
      ...prev,
      [type]: theme,
    }));
  };

  const handleQuestionCountChange = (type, count) => {
    setQuestionCounts((prev) => ({
      ...prev,
      [type]: count,
    }));
  };

  const updateProgress = (value) => {
    if (progressRef.current) {
      progressRef.current.style.width = `${value}%`;
    }
    if (progressPercentRef.current) {
      progressPercentRef.current.textContent = `${Math.round(value)}%`;
    }
  };

  const extractJsonFromResponse = (response) => {
    if (Array.isArray(response) || typeof response === "object") {
      return response;
    }
    if (typeof response === "string") {
      try {
        return JSON.parse(response);
      } catch (e) {
        console.error("Error parsing JSON:", e);
        throw new Error("Failed to parse JSON from the response");
      }
    }
    throw new Error("Unexpected response format");
  };

  const parseDTKQuestions = (response) => {
    // Ensure the response is a string
    if (typeof response !== "string") {
      throw new Error("Response must be a string");
    }

    // Replace LaTeX backslashes with a temporary placeholder
    const tempResponse = response.replace(/\\/g, "||BACKSLASH||");

    try {
      // Parse the modified JSON
      const parsed = JSON.parse(tempResponse);

      // Restore the LaTeX backslashes
      const restoreBackslashes = (obj) => {
        for (const key in obj) {
          if (typeof obj[key] === "string") {
            obj[key] = obj[key].replace(/\|\|BACKSLASH\|\|/g, "\\");
          } else if (typeof obj[key] === "object") {
            restoreBackslashes(obj[key]);
          }
        }
      };

      restoreBackslashes(parsed);
      return parsed;
    } catch (error) {
      console.error("Error parsing DTK questions:", error);
      throw new Error("Failed to parse DTK questions");
    }
  };

  const generatePrompt = (type) => {
    const theme =
      selectedThemes[type] !== "AI får välja"
        ? `inom kategorin: ${selectedThemes[type]}.`
        : "";
    const count = questionCounts[type];
    let diff =
      difficulty === "hard"
        ? "Extremt svåra ord, ytterst sällsynta."
        : difficulty;

    const gradeInstructionsText = gradeInstructions[grade];
    let finalGrade = "";

    if (user.accountType === "teacher") {
      finalGrade = `Anpassa efter följande årskurs: ${gradeInstructionsText}.`;
    } else {
      finalGrade = "";
    }

    switch (type) {
      case "ORD":
        return `Generera ${count} frågor för ORD-sektionen (ordförståelse) för det svenska Högskoleprovet, exempel på ord: impertinent, verserad, renegat, juvenil - men använd inte dessa, de är bara exempel. Frågorna består endast av ett  ord på ${selectedLanguage}  sen ska användaren välja den närmaste  synonymen. Alla ord och alterantiv ska vara på ${selectedLanguage} ${theme}.  ${finalGrade}.  Följ denna JSON-struktur varje gång: [{"question": "word", "options": ["option1", "option2", "option3", "option4", "option5"], "correctAnswer": "correctOption"}]`;
      case "LÄS":
        return `Generera ${count} frågor för LÄS-sektionen (svensk läsförståelse) för det svenska Högskoleprovet. ${finalGrade}. Skriv allt på ${selectedLanguage} ${theme}. Texten ska vara minst 300 ord indelade i stycken med radbrytningar. The response should be an array of JSON objects with the following structure: [{"text": "passage", "questions": [{"question": "question text", "options": ["option1", "option2", "option3", "option4"], "correctAnswer": "correctOption"}]}]`;
      case "MEK":
        return `Generera ${count} frågor för MEK-sektionen (meningskomplettering) för det svenska Högskoleprovet.  ${finalGrade}. Skriv allt på ${selectedLanguage} ${theme}. Det kan antingen vara en eller två luckor man ska fylla i det saknade ordet/orden för. Är det två luckor ska det finnas två ord per svarsalternativ (exempel: kriga - anpassa).  The response should be an array of JSON objects with the following structure: [{"question": "sentence with blanks", "options": ["option1", "option2", "option3", "option4"], "correctAnswer": "correctOption"}]`;
      case "ELF":
        return `Generera ${count} frågor för ELF-sektionen (engelsk läsförståelse) för det svenska Högskoleprovet.  ${finalGrade}. Skriv allt på ${selectedLanguage} ${theme}. Texten ska vara minst 300 ord indelade i stycken med radbrytningar. The response should be an array of JSON objects with the following structure: [{"text": "English passage", "questions": [{"question": "question text", "options": ["option1", "option2", "option3", "option4"], "correctAnswer": "correctOption}]}]`;
      case "XYZ":
        // const mathTheme = selectedThemes[type];
        // let problemTypes;
        // if (mathTheme === "AI får välja") {
        //   problemTypes = '"algebra|geometry|arithmetic|calculus|trigonometry"';
        // } else {
        //   // Map Swedish terms to English for consistency with original structure
        //   const themeMap = {
        //     Algebra: "algebra",
        //     Geometri: "geometry",
        //     Aritmetik: "arithmetic",
        //     Kalkyl: "calculus",
        //     Trigonometri: "trigonometry",
        //   };
        //   problemTypes = `"${themeMap[mathTheme] || mathTheme.toLowerCase()}"`;
        // }
        return `Generera ${count} matematikfrågor. ${finalGrade}
---
När du använder LaTeX-syntax MÅSTE du omsluta VARJE matematiskt uttryck (oavsett om det är enkla operationer eller komplexa ekvationer) med dollartecken på följande sätt:

För uttryck inom textrader: $x \\times y$
För fristående uttryck: $$x \\times y$$

Använd ALDRIG [], () eller andra tecken runt matematiska uttryck. ALLTID dollartecken.

          ---

          Svaret ska vara ett JSON-objekt med följande struktur: 
  [
    {

      "question": "Beskrivning av problemet eller frågan",
      "options": ["alternativ1", "alternativ2", "alternativ3", "alternativ4"],
      "correctAnswer": "korrekt alternativ",
    }
  ]`;

      case "DTK":
        const mathTheme2 = selectedThemes[type];
        let problemTypes2;
        if (mathTheme2 === "AI får välja") {
          problemTypes2 =
            '"algebra|geometry|probability|calculus|statistics|functions|trigonometry|dataAnalysis|linearAlgebra"';
        } else {
          const themeMap = {
            Algebra: "algebra",
            Geometri: "geometry",
            Sannolikhet: "probability",
            Kalkyl: "calculus",
            Statistik: "statistics",
            Funktioner: "functions",
            Trigonometri: "trigonometry",
            Dataanalys: "dataAnalysis",
            "Linjär algebra": "linearAlgebra",
          };
          problemTypes2 = `"${themeMap[mathTheme2] || mathTheme2.toLowerCase()}"`;
        }

        return `Generera ${count} matematikfrågor med svårighetsgrad ${diff} inom området ${mathTheme2}.

IMPORTANT:
1. Use only pure numeric values. No mathematical functions in the data itself.
2. Generate data that is well-suited for the specified chart type and showcases interesting patterns such as linear growth, exponential change, periodic behavior, or step functions when appropriate for the problem type.
3. DO NOT PRESENT THE ANSWER IN THE GRAPH!

     Svaret ska vara ett JSON-objekt med följande struktur: 
    [
      {
        "difficulty": "${diff}",
        "problemType": "statistics|probability|functions|dataAnalysis|timeSeries|financialMath|discreteMath|setTheory|graphTheory|numericalMethods|optimization|regressionAnalysis|experimentalDesign|sampling|informationTheory|gameTheory|operationsResearch|networkAnalysis",
        "question": "Frågan på svenska",
        "chartType": "LineChart|BarChart|ScatterChart|AreaChart|PieChart",
        "chartData": [
          {
            "x": 1,
            "y": 2,
            "z": 3
          // Ytterligare datapunkter vid behov. Använd endast numeriska värden, inga matematiska funktioner.
          }
          // Fler dataobjekt vid behov
        ],
        "chartConfig": {
          "xAxisLabel": "Etikett för x-axeln",
          "yAxisLabel": "Etikett för y-axeln",
          "series": [
            {
              "dataKey": "y",
              "name": "Serie 1",
              "color": "#8884d8"
            }
            // Fler serier vid behov
          ]
        },
        "options": [
          "Alternativ A",
          "Alternativ B",
          "Alternativ C",
          "Alternativ D"
        ],
        "correctAnswer": "Det korrekta alternativet"
      }
    ]`;

      default:
        return "";
    }
  };

  const simulateProgress = (startProgress, endProgress, duration) => {
    const stepSize = 0.5;
    const steps = (endProgress - startProgress) / stepSize;
    const stepDuration = duration / steps;
    let currentProgress = startProgress;

    const intervalId = setInterval(() => {
      currentProgress += stepSize;
      if (currentProgress >= endProgress) {
        clearInterval(intervalId);
      } else {
        updateProgress(currentProgress);
      }
    }, stepDuration);

    return () => clearInterval(intervalId);
  };

  const handleGenerateQuestions = async (event) => {
    event.preventDefault();
    setIsGenerating(true);
    setGenerationStatus({});
    setGeneratedTestId(null);
    updateProgress(0);
    setCurrentGeneratingType("");

    // setTimeout(() => {
    //   const progressBar = document.querySelector(".progress-bar-container");
    //   if (progressBar) {
    //     progressBar.scrollIntoView({ behavior: "smooth", block: "start" });
    //   }
    // }, 500);

    Logger.log("Generating questions with difficulty:", difficulty);
    Logger.log("Selected themes:", selectedThemes);
    Logger.log("Selected language:", selectedLanguage);
    Logger.log("Selected model:", selectedModel);

    try {
      const totalTypes = selectedQuestionTypes.length;
      const results = [];

      for (const [index, type] of selectedQuestionTypes.entries()) {
        setCurrentGeneratingType(type);
        setGenerationStatus((prev) => ({ ...prev, [type]: "genererar" }));

        const startProgress = (index / totalTypes) * 100;
        const endProgress = ((index + 1) / totalTypes) * 100;

        const stopSimulation = simulateProgress(
          startProgress,
          endProgress,
          15000,
        );

        const prompt = generatePrompt(type);
        let response = await callApi(
          [
            {
              role: "system",
              content:
                "Du är en AI-assistent som genererar frågor för det svenska Högskoleprovet för att hjälpa elever träna på simulerade frågor. Respond with only the JSON array, no additional text.",
            },
            { role: "user", content: prompt },
          ],
          undefined,
          undefined,
          user.uid,
        );

        stopSimulation();

        Logger.log("Raw response type:", typeof response);
        Logger.log("Raw response:", response);

        if (typeof response === "string") {
          if (response.startsWith("```json")) {
            response = response.slice(7, -3).trim();
            Logger.log("removed 3 characters from json");
          } else if (response.startsWith("```")) {
            response = response.slice(3, -3).trim();
          }
        }

        let parsedQuestions;
        if (type === "XYZ") {
          try {
            parsedQuestions = parseDTKQuestions(response);
            Logger.log("Parsed DTK questions:", parsedQuestions);
          } catch (error) {
            console.error(`Error processing DTK questions:`, error);
            Logger.log("Raw response:", response);
            throw new Error(`Failed to process DTK questions`);
          }
        } else {
          // Use the existing extractJsonFromResponse for other question types
          parsedQuestions = extractJsonFromResponse(response);
        }

        setGenerationStatus((prev) => ({ ...prev, [type]: "klar" }));
        results.push({ questionType: type, questions: parsedQuestions });

        updateProgress(endProgress);
      }

      const finalJson = {
        questions: [],
      };

      results.forEach((result) => {
        if (result.questionType === "LÄS" || result.questionType === "ELF") {
          result.questions.forEach((q) => {
            finalJson.questions.push({
              questionType: result.questionType,
              text: q.text,
              questions: q.questions,
            });
          });
        } else if (result.questionType === "XYZ") {
          finalJson.questions.push({
            questionType: "XYZ",
            questions: result.questions,
          });
        } else {
          finalJson.questions.push(result);
        }
      });

      const generate6DigitId = () => {
        const fullUuid = uuidv4();
        const numbers = fullUuid.replace(/[^0-9]/g, "");
        return numbers.slice(0, 6);
      };

      const sixDigitId = generate6DigitId();
      const name = `Hp-${sixDigitId}`;

      // First, create a new document reference with a specific ID
      const newDocId = doc(collection(db, "hpTestData")).id;

      // Use this ID to add the document to hpTestData
      await setDoc(doc(db, "hpTestData", newDocId), {
        uid: user.uid,
        name: name,
        data: finalJson,
      });

      const hpPublicData = {
        difficulty: difficulty,
        selectedThemes: selectedThemes,
        selectedLanguage: selectedLanguage,
        selectedModel: selectedModel,
        originallyCreatedByUserId: user.uid,
        refToOriginalTestId: newDocId,
        official: false,
        createdAt: Date.now(),
        name: name,
        data: finalJson,
        upvotes: 0,
        downvotes: 0,
        userVotes: {},
      };

      // Use the same ID to add the document to hpPublic
      await setDoc(doc(db, "hpPublic", newDocId), hpPublicData);

      await trackLesson(user.uid, "HP.js", null, null);

      Logger.log(JSON.stringify(hpPublicData, null, 2));
      Logger.log("finalJson: ", JSON.stringify(finalJson, null, 2));

      setGeneratedTestId(newDocId);
      updateProgress(0);
      navigate(`/app/hp/${newDocId}`);
    } catch (error) {
      console.error("Error generating questions:", error);
      showSnackbar(
        `Nåt gick fel när AI-modellen genererade frågorna. Det kan ske ibland. Klicka på Generera igen.`,
        "info",
        5000,
      );
      setGenerationStatus((prev) => ({ ...prev, error: error.message }));
    } finally {
      setIsGenerating(false);
      updateProgress(100);
      setCurrentGeneratingType("");
    }
  };

  return (
    <>
      <InfoBox
        instructions={[]}
        theme={theme}
        styles={styles}
        user={user}
        hp={true}
      />

      <form onSubmit={handleGenerateQuestions}>
        {currentStep === 1 && (
          <>
            <AnimatedStep isRevealed={true}>
              <Box sx={{ ...styles.headlineLessonBox, mt: 10 }}>
                <Typography variant="h6" sx={styles.headlineLessonBoxTitle}>
                  <span>1</span> Välj frågetyper
                </Typography>
              </Box>

              <Box
                sx={{
                  ...styles.freshBox,
                  ...styles.softBoxShadow,
                  padding: "30px",
                }}
              >
                <Grid container spacing={2}>
                  {[
                    "ORD",
                    "LÄS",
                    "MEK",
                    ...(user.accountType === "student" ? ["ELF"] : []),
                    "XYZ",
                  ].map((type) => (
                    <Grid item xs={12} sm={3} md={3} key={type}>
                      <QuestionTypeSelector
                        type={type}
                        user={user}
                        selectedQuestionTypes={selectedQuestionTypes}
                        handleQuestionTypeChange={handleQuestionTypeChange}
                        selectedThemes={selectedThemes}
                        handleThemeChange={handleThemeChange}
                        questionCounts={questionCounts}
                        handleQuestionCountChange={handleQuestionCountChange}
                        theme={theme}
                        questionTypeDescriptions={questionTypeDescriptions}
                        showSnackbar={showSnackbar}
                      />
                    </Grid>
                  ))}
                </Grid>
                {selectedQuestionTypes.length > 0 && (
                  <Box sx={{ textAlign: "center", mt: 4, mb: 2 }}>
                    <Button
                      variant="purp"
                      onClick={() => {
                        if (selectedQuestionTypes.length > 0) {
                          setCurrentStep(2);
                        } else {
                          showSnackbar("Välj  minst en frågetyp.", "info");
                        }
                      }}
                      disabled={selectedQuestionTypes.length === 0}
                      endIcon={<ArrowForwardIosIcon />}
                    >
                      Nästa
                    </Button>
                  </Box>
                )}
              </Box>
            </AnimatedStep>
          </>
        )}

        {currentStep === 2 && (
          <>
            <BackButton
              onClick={() => {
                setCurrentStep(currentStep - 1);
              }}
              show={currentStep !== 1}
            />
            <AnimatedStep isRevealed={true}>
              <Box sx={{ ...styles.headlineLessonBox, mt: 8 }}>
                <Typography variant="h6" sx={styles.headlineLessonBoxTitle}>
                  <span>2</span> Generera provet
                </Typography>
              </Box>

              <Box
                sx={{
                  ...styles.freshBox,
                  ...styles.softBoxShadow,
                  padding: "40px",
                  width: { xs: "100%", md: "70%" },
                  margin: "0 auto",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} sx={{ display: "none" }}>
                    <FormControl fullWidth>
                      <Typography
                        variant="formLabelSinUnderline"
                        sx={{ fontWeight: "500", mt: 1 }}
                      >
                        Svårighetsgrad:
                      </Typography>
                      <Select
                        value={difficulty}
                        sx={{ mt: 1 }}
                        onChange={(e) => setDifficulty(e.target.value)}
                      >
                        <MenuItem value="easy">Enkel</MenuItem>
                        <MenuItem value="medium">Medelsvår</MenuItem>
                        <MenuItem value="hard">Svår</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <Typography
                        variant="formLabelSinUnderline"
                        sx={{ fontWeight: "500", mt: 0 }}
                      >
                        Språk:
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <LanguageSelector
                          language={selectedLanguage}
                          setLanguage={setSelectedLanguage}
                        />
                      </Box>
                    </FormControl>
                  </Grid>
                  {user.accountType === "teacher" && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "500", mt: 0 }}
                      >
                        Årskurs:
                      </Typography>
                      <Box sx={{ mt: 0.5, mb: 2 }}>
                        <GradeSelector grade={grade} setGrade={setGrade} />
                      </Box>
                    </Grid>
                  )}
                  <Grid
                    item
                    xs={12}
                    md={user.accountType === "teacher" ? 12 : 6}
                  >
                    <Typography
                      variant="formLabelSinUnderline"
                      sx={{ fontWeight: "500", mt: 1 }}
                    >
                      AI-modell
                      <AIModelInfoTooltip>
                        <span style={{ cursor: "help" }}>
                          <QuestionMarkIcon
                            sx={{
                              fontSize: 16,
                              ml: 1,
                              verticalAlign: "middle",
                            }}
                          />
                        </span>
                      </AIModelInfoTooltip>
                    </Typography>

                    <AIModelSelect
                      selectedModel={selectedModel}
                      setSelectedModel={setSelectedModel}
                      disabledOptions={["Gemini Pro", "Mistral Large"]}
                    />
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    mt: 5,
                    display: "flex",
                    justifyContent: "center", // This centers the button horizontally
                  }}
                  className="progress-bar-container"
                >
                  <Button
                    type="submit"
                    variant={
                      theme.palette.mode === "light" ? "purp" : "contained"
                    }
                    color="primary"
                    disabled={
                      isGenerating || selectedQuestionTypes.length === 0
                    }
                    startIcon={
                      isGenerating ? (
                        <CircularProgress size={18} />
                      ) : (
                        <GenerateIcon
                          style={{
                            color:
                              theme.palette.mode === "dark"
                                ? "white"
                                : "#f9f9f9",
                          }}
                        />
                      )
                    }
                  >
                    {isGenerating ? "Genererar..." : "Generera provet"}
                  </Button>
                </Box>
              </Box>
              <Box
                sx={{
                  width: "100%",
                  mb: 2,
                  mt: 4,

                  padding: "20px",
                  borderRadius: "10px",
                  display:
                    isGenerating || selectedQuestionTypes.length > 0
                      ? "block"
                      : "none",
                }}
              >
                {isGenerating && (
                  <>
                    <Dialog
                      open={isGenerating}
                      disableEscapeKeyDown
                      onClick={handleBackdropClick}
                      PaperProps={{
                        elevation: 0,
                        sx: {
                          minWidth: { xs: "100%", md: "600px" },
                          minHeight: { xs: "30%", md: "350px" },
                          backgroundColor: "background.paper",
                          boxShadow: 24,
                          borderRadius: 4,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        },
                        onClick: (e) => e.stopPropagation(), // Prevent click from bubbling to backdrop
                      }}
                    >
                      <DialogContent
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            p: 4,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 3,
                          }}
                        >
                          <svg width={0} height={0}>
                            <defs>
                              <linearGradient
                                id="my_gradient"
                                x1="0%"
                                y1="0%"
                                x2="0%"
                                y2="100%"
                              >
                                <stop offset="0%" stopColor="#e01cd5" />
                                <stop offset="100%" stopColor="#1CB5E0" />
                              </linearGradient>
                            </defs>
                          </svg>

                          {/* CircularProgress with gradient */}
                          <CircularProgress
                            size={58}
                            sx={{
                              "svg circle": { stroke: "url(#my_gradient)" },
                            }}
                          />

                          <Typography
                            variant="h5"
                            sx={{
                              textAlign: "center",
                              fontWeight: "600",
                              fontSize: "1.3rem",
                              mt: 3,
                              mb: 3,
                            }}
                          >
                            Genererar provet. Snart klart...
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ...styles.h5, mb: 1 }}
                          >
                            Genererar frågor för{" "}
                            <strong>{currentGeneratingType}</strong>
                          </Typography>
                          <Box
                            sx={{
                              width: "100%",
                              height: "20px",
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "#22303c"
                                  : "white",
                              borderRadius: "8px",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              ref={progressRef}
                              sx={{
                                width: "0%",
                                height: "100%",
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "#6F38EA"
                                    : "#6339c4",
                                transition: "width 0.1s linear",
                              }}
                            />
                          </Box>
                          <Typography
                            ref={progressPercentRef}
                            variant="body2"
                            color="text.secondary"
                            align="right"
                          >
                            0%
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "nowrap",
                              overflow: "auto",
                            }}
                          >
                            {selectedQuestionTypes.map((type) => (
                              <Chip
                                key={type}
                                label={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {type}:{" "}
                                    {generationStatus[type] === "klar" ? (
                                      <CheckCircleOutlineIcon
                                        fontSize="small"
                                        sx={{ ml: 0.5 }}
                                      />
                                    ) : (
                                      generationStatus[type] || "Redo"
                                    )}
                                  </Box>
                                }
                                color={
                                  generationStatus[type] === "klar"
                                    ? "success"
                                    : "default"
                                }
                                sx={{
                                  mr: 1,
                                  backgroundColor: "#2b822f",
                                  color: "white",
                                  fontWeight: "600",
                                  animation:
                                    generationStatus[type] === "genererar"
                                      ? `${pulse} 1.5s ease-in-out infinite`
                                      : "none",
                                }}
                              />
                            ))}
                          </Box>
                          {generationStatus.error && (
                            <Box sx={{ mt: 2 }}>
                              <Typography color="error">
                                Knag i fogarna, försök igen:{" "}
                                {generationStatus.error}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
              </Box>
            </AnimatedStep>
          </>
        )}
      </form>
      <Box sx={{ width: { xs: "100%", md: "70%" }, margin: "0 auto", mt: 24 }}>
        <Box sx={{ ...styles.headlineLessonBox }}>
          <Typography
            variant="h6"
            sx={{ ...styles.headlineLessonBoxTitle, fontWeight: 500 }}
          >
            Såhär ser ett prov ut
          </Typography>
        </Box>

        <VideoPlayer
          videoUrl="https://sportkuriren.se/wp-content/uploads/hp.mp4"
          poster="https://sportkuriren.se/wp-content/uploads/hp.png"
          autoplayOnScroll={true}
        />

        <Box sx={{ ...styles.headlineLessonBox, mt: 12 }}>
          <Typography
            variant="h6"
            sx={{ ...styles.headlineLessonBoxTitle, fontWeight: 500 }}
          >
            Skapa prov & dela med elever
          </Typography>
        </Box>

        <VideoPlayer
          videoUrl="https://sportkuriren.se/wp-content/uploads/hp-skapa-prov.mp4"
          poster="https://sportkuriren.se/wp-content/uploads/hp-skapa-prov.png"
          autoplayOnScroll={true}
        />
      </Box>
      <Box sx={{ mt: 20, display: "none" }}>
        <Alert severity="warning" sx={{ mt: 2, mb: 0, borderRadius: "30px" }}>
          <strong>BETA: </strong> Vi jobbar på att få bättre resultat från vår
          AI-modell. (Du kommer även kunna generera de kvantitativa delarna inom
          kort.)
        </Alert>
      </Box>
    </>
  );
};

export default GenerateHP;
